import { connectIntegration } from '../../lib/integrationConnect'
import { ApiError } from '../../lib/api'
import { posthog } from '../../lib/posthog'
import { Button } from '../../components/ui/button'
import type { SettingsCardProps } from '../index'
import { DisconnectButton, IntegrationCard } from '../ui'
import { figmaApi, figmaStatusOf } from './api'
import { FigmaTile } from './Tile'
import { FigmaTokenForm } from './TokenForm'

/** Figma's card on the Integrations page: OAuth when the server has a Figma
 *  app, and the personal-access-token way in always (the only way on a
 *  self-hosted install, the fallback behind a link elsewhere). */
export function FigmaSettingsCard({
  status,
  busy,
  setBusy,
  setStatus,
  showToast,
  requestDisconnect,
}: SettingsCardProps) {
  const figma = figmaStatusOf(status)

  async function connect() {
    if (busy) return
    setBusy(true)
    try {
      const destination = await connectIntegration('figma', () =>
        posthog.capture('integration_connect_started', { provider: 'figma' }),
      )
      if (destination === 'browser') setBusy(false)
    } catch (error) {
      console.error(error)
      showToast('Couldn’t start the Figma connection')
      setBusy(false)
    }
  }

  /** Resolves to the reason when Figma turns the token down. */
  async function connectToken(token: string): Promise<string | null> {
    setBusy(true)
    try {
      setStatus(await figmaApi.connectToken(token))
      posthog.capture('integration_connected', { provider: 'figma', via: 'token' })
      return null
    } catch (error) {
      return error instanceof ApiError ? String(error.body.error ?? 'Couldn’t connect') : 'Couldn’t connect'
    } finally {
      setBusy(false)
    }
  }

  return (
    <IntegrationCard
      tile={<FigmaTile size={36} />}
      name="Figma"
      detail={
        figma.connected
          ? `${figma.accountName ?? 'Figma'} · ${figma.via === 'oauth' ? 'signed in' : 'access token'}`
          : 'Import your designs'
      }
      connected={figma.connected}
      footnote={figma.connected ? 'Import from any canvas' : ''}
      actions={
        figma.connected ? (
          <>
            {figma.oauth && (
              <Button variant="bare" size="sm" disabled={busy} onClick={() => void connect()}>
                Reconnect
              </Button>
            )}
            <DisconnectButton disabled={busy} onClick={requestDisconnect} />
          </>
        ) : figma.oauth ? (
          <Button variant="default" size="sm" disabled={busy} onClick={() => void connect()}>
            Connect
          </Button>
        ) : null
      }
    >
      <FigmaTokenForm
        busy={busy}
        collapsed={figma.connected || figma.oauth}
        openLabel={figma.connected ? 'Replace access token' : undefined}
        onSubmit={connectToken}
      />
    </IntegrationCard>
  )
}
